import { useState, useEffect } from 'react'
import { Phone, PhoneOff, PhoneIncoming, Mic, MicOff, X, Delete } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { usePermissions } from '@/hooks/use-permissions'
import { useSoftphone } from '@/hooks'
import { useSoftphoneStore, useToast } from '@/stores'

function formatPhone(raw: string): string {
  const d = (raw || '').replace(/\D/g, '')
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d
  if (ten.length === 10) return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`
  return raw || 'Unknown'
}

/**
 * Global browser softphone. Renders a floating control only when calling is
 * actually available (Twilio configured + device registered) or a call is in
 * progress — so it stays invisible until the org wires up Twilio Voice.
 * Gated to users with Service Intake access.
 */
export function SoftphoneWidget() {
  const { canAccess } = usePermissions()
  const allowed = canAccess('service_intake')
  const sp = useSoftphone(allowed)
  const [open, setOpen] = useState(false)
  const [dialInput, setDialInput] = useState('')

  // Outbound-call requests from elsewhere (e.g. a WO contact's Call button)
  // are routed through the store so they use this one device, not a new one.
  const pendingDial = useSoftphoneStore((s) => s.pendingDial)
  const clearPendingDial = useSoftphoneStore((s) => s.clearPendingDial)
  const toast = useToast()
  const { status: spStatus, dial: spDial } = sp
  useEffect(() => {
    if (!pendingDial) return
    if (!allowed) { clearPendingDial(); return }
    if (spStatus === 'ready') {
      spDial(pendingDial); clearPendingDial()
    } else if (spStatus === 'on-call') {
      toast.error('You’re already on a call'); clearPendingDial()
    } else if (spStatus === 'offline' || spStatus === 'error' || spStatus === 'idle') {
      toast.error('Calling isn’t set up yet'); clearPendingDial()
    }
    // 'connecting' / 'incoming' → wait for the next status change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDial, spStatus, allowed])

  if (!allowed) return null
  // Dormant (not configured / failed / connecting before first ready): show nothing.
  if (sp.status === 'idle' || sp.status === 'offline' || sp.status === 'error' || sp.status === 'connecting') return null

  const toE164 = (raw: string): string | null => {
    const d = raw.replace(/\D/g, '')
    if (d.length === 10) return `+1${d}`
    if (d.length === 11 && d.startsWith('1')) return `+${d}`
    if (raw.trim().startsWith('+')) return raw.trim()
    return null
  }

  // Incoming call — always surface a prominent prompt.
  if (sp.status === 'incoming') {
    return (
      <div className="fixed bottom-4 right-4 z-50 w-72 rounded-xl border bg-background shadow-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <PhoneIncoming className="h-5 w-5 text-green-600 animate-pulse" />
          <div className="min-w-0">
            <p className="text-sm font-semibold">Incoming call</p>
            <p className="text-xs text-muted-foreground truncate">{formatPhone(sp.incomingFrom ?? '')}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={sp.answer}>
            <Phone className="h-4 w-4 mr-1" /> Answer
          </Button>
          <Button variant="destructive" className="flex-1" onClick={sp.decline}>
            <PhoneOff className="h-4 w-4 mr-1" /> Decline
          </Button>
        </div>
      </div>
    )
  }

  // On a call — controls.
  if (sp.status === 'on-call') {
    return (
      <div className="fixed bottom-4 right-4 z-50 w-72 rounded-xl border bg-background shadow-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" /></span>
          <div className="min-w-0">
            <p className="text-sm font-semibold">On call</p>
            <p className="text-xs text-muted-foreground truncate">{formatPhone(sp.activeNumber ?? '')}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={sp.toggleMute}>
            {sp.muted ? <MicOff className="h-4 w-4 mr-1" /> : <Mic className="h-4 w-4 mr-1" />}
            {sp.muted ? 'Unmute' : 'Mute'}
          </Button>
          <Button variant="destructive" className="flex-1" onClick={sp.hangup}>
            <PhoneOff className="h-4 w-4 mr-1" /> Hang up
          </Button>
        </div>
      </div>
    )
  }

  // Ready — collapsed FAB that expands into a dialer.
  return (
    <div className="fixed bottom-4 right-4 z-50">
      {open ? (
        <div className="w-64 rounded-xl border bg-background shadow-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold">Dialer</p>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOpen(false)}><X className="h-4 w-4" /></Button>
          </div>
          <div className="flex gap-1">
            <Input
              value={dialInput}
              onChange={(e) => setDialInput(e.target.value)}
              placeholder="(512) 555-1234"
              inputMode="tel"
              onKeyDown={(e) => { if (e.key === 'Enter') { const n = toE164(dialInput); if (n) sp.dial(n) } }}
            />
            <Button variant="ghost" size="icon" onClick={() => setDialInput((v) => v.slice(0, -1))}><Delete className="h-4 w-4" /></Button>
          </div>
          <Button
            className="w-full mt-2 bg-green-600 hover:bg-green-700"
            disabled={!toE164(dialInput)}
            onClick={() => { const n = toE164(dialInput); if (n) sp.dial(n) }}
          >
            <Phone className="h-4 w-4 mr-1" /> Call
          </Button>
        </div>
      ) : (
        <Button size="icon" className="h-12 w-12 rounded-full shadow-lg" onClick={() => setOpen(true)} title="Dialer">
          <Phone className="h-5 w-5" />
        </Button>
      )}
    </div>
  )
}
